import React from 'react';

import WorkflowArticleLayout from './components/WorkflowArticleLayout';
import WorkflowChatBubble from './components/WorkflowChatBubble';
import WorkflowCodeBlock from './components/WorkflowCodeBlock';
import WorkflowInfoBox from './components/WorkflowInfoBox';
import WorkflowLearningPoints from './components/WorkflowLearningPoints';
import WorkflowList from './components/WorkflowList';
import WorkflowOutputBox from './components/WorkflowOutputBox';
import WorkflowPracticeBox from './components/WorkflowPracticeBox';
import WorkflowSection from './components/WorkflowSection';
import WorkflowStepFlow from './components/WorkflowStepFlow';
import WorkflowTable from './components/WorkflowTable';
import WorkflowTableOfContents from './components/WorkflowTableOfContents';
import WorkflowText from './components/WorkflowText';
import WorkflowTipBox from './components/WorkflowTipBox';

const beSteps = [
  {
    label: 'Part1',
    title: 'スキーマ定義とAPI仕様書を作成する',
    path: '/devdocs/workflow/be-step1',
  },
  { label: 'Part2', title: 'API実装ワークフローとその全体像', path: '/devdocs/workflow/be-step2' },
  { label: 'Part3', title: 'Phase1: DD書の作成', path: '/devdocs/workflow/be-step3' },
  { label: 'Part4', title: 'Phase2: コード生成', path: '/devdocs/workflow/be-step4' },
  { label: 'Part5', title: 'Phase3: 単体テスト', path: '/devdocs/workflow/be-step5' },
  {
    label: 'Part6',
    title: 'E2Eテスト',
    path: '/devdocs/workflow/be-step6',
    active: true,
  },
  { label: '付録', title: '成果物サンプル集', path: '/devdocs/workflow/be-appendix' },
];

const WorkflowBeStep6: React.FC = () => {
  return (
    <WorkflowArticleLayout
      title="バックエンド開発編 Part6 — E2Eテスト: 機能横断シナリオの検証"
      seriesName="バックエンド開発編"
      tags={['Backend', 'E2E', 'Test']}
      seriesSteps={beSteps}
      prevLink={{ label: 'Part5: Phase3 単体テスト', path: '/devdocs/workflow/be-step5' }}
      nextLink={{ label: '付録: 成果物サンプル集', path: '/devdocs/workflow/be-appendix' }}
    >
      <WorkflowLearningPoints
        items={[
          'E2Eテストケースの設計方針（単体テストとの違い）',
          'create_e2e_testcase.md による業務シナリオ型テストケースの生成方法',
          'テスト実行〜バグ検知時の結果記録ルール',
          'テストケースの期待値修正と実装バグの切り分け',
        ]}
      />

      <WorkflowTableOfContents
        items={[
          { id: 'section-1', label: '概要' },
          { id: 'section-2', label: '単体テストとE2Eテストの違い' },
          { id: 'section-3', label: 'E2Eテストの全体フロー' },
          { id: 'section-4', label: 'テストケースの設計: create_e2e_testcase.md' },
          { id: 'section-5', label: 'テストケースの構造' },
          { id: 'section-6', label: 'テストの実行' },
          { id: 'section-7', label: 'バグ検知時の対応' },
          { id: 'section-8', label: '本プロジェクトでの実体験' },
          { id: 'section-9', label: '関連するステアリングファイル' },
          { id: 'section-10', label: '注意すべきこと' },
          { id: 'section-11', label: '成果物まとめ' },
          { id: 'section-12', label: 'ベストプラクティスとアンチパターン' },
          { id: 'section-13', label: 'バックエンド開発編の全体まとめ' },
        ]}
      />

      {/* セクション1: 概要 */}
      <WorkflowSection title="概要" id="section-1" number={1}>
        <WorkflowText>
          E2Eテストは、単体テスト（Phase3）で個別に検証済みのAPIを業務フローとして組み合わせて実行し、機能横断でのデータ整合性やステータス遷移を検証するフェーズです。
        </WorkflowText>
        <WorkflowText>
          単体テストが「1つのAPIが仕様通りに動くか」を確認するのに対し、E2Eテストは「複数APIを業務順序で呼び出した時に全体として正しく動作するか」を確認します。
        </WorkflowText>
      </WorkflowSection>

      {/* セクション2: 単体テストとE2Eテストの違い */}
      <WorkflowSection title="単体テストとE2Eテストの違い" id="section-2" number={2}>
        <WorkflowTable
          headers={['観点', '単体テスト（Phase3）', 'E2Eテスト（Part6）']}
          rows={[
            ['スコープ', '1API単体', '複数APIの組み合わせ'],
            ['テストケース設計', 'API仕様書ベース', 'usecases.md ベース'],
            [
              '確認内容',
              'バリデーション、認証/認可、個別エラー',
              '業務フロー、データライフサイクル、ドメイン横断整合性',
            ],
            ['前提データ', 'テストケースごとに独立', '前のケースのデータを後続ケースで再利用'],
            ['実施タイミング', 'API実装直後（Phase3）', '関連API群の単体テスト全件完了後'],
            ['ルール定義', 'api_project_rule（Phase3）', 'create_e2e_testcase.md（手動起動）'],
          ]}
        />

        <WorkflowTipBox title="なぜ単体テスト完了後にE2Eテストが必要なのか">
          単体テストでは各APIが仕様通りに動作することを確認しています。しかし、実際の業務では複数APIが順序立てて呼び出されます。
          {'\n\n'}
          例えば「パートナー登録→要員登録→プロジェクト登録→契約申請→承認」という業務フローでは、各APIが個別にOKでも以下の問題が残ります:
          {'\n\n'}
          ・データ連携: パートナーIDを要員登録に渡す時のフォーマットは合っているか
          {'\n'}
          ・ステータス遷移: 契約申請のADJUSTING→SUBMITTED→IN_PROGRESS→APPROVEDの全遷移が正しいか
          {'\n'}
          ・副作用の確認: 契約承認後にcontract + contract_subが正しく自動作成されるか
          {'\n'}
          ・除外ロジック: 契約終了後に一覧から正しく除外されるか
          {'\n\n'}
          単体テストは「木を見る」テスト、E2Eテストは「森を見る」テストです。
        </WorkflowTipBox>
      </WorkflowSection>

      {/* セクション3: E2Eテストの全体フロー */}
      <WorkflowSection title="E2Eテストの全体フロー" id="section-3" number={3}>
        <WorkflowText>
          E2Eテストは api_project_rule
          とは独立したプロセスです。複数APIが揃った後に業務シナリオ全体を検証するフェーズとして別途実施します。
        </WorkflowText>
        <WorkflowText>
          また、一度作成した .e2e.md
          はリグレッション試験としても再利用できます。既存APIの修正やリファクタリングを行った後に同じテストケースを再実行することで、修正が他の業務フローに影響を与えていないことを確認できます。
        </WorkflowText>

        <WorkflowSection title="初回実施フロー" subNumber="3.1">
          <WorkflowStepFlow
            steps={[
              { label: 'テストケース設計（.e2e.md 生成）' },
              { label: 'ユーザー確認 → 修正があれば指示', highlight: true },
              { label: 'テスト実行 → result_{yyyyMMddHHmmss}.md 出力' },
              { label: '結果レビュー → NG時は対応判断', highlight: true },
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="リグレッション試験フロー" subNumber="3.2">
          <WorkflowStepFlow
            steps={[
              { label: '既存 .e2e.md をそのまま使用' },
              { label: 'テスト実行 → result_{yyyyMMddHHmmss}.md 出力' },
              { label: '結果レビュー → 既存機能への影響確認', highlight: true },
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション4: テストケースの設計 */}
      <WorkflowSection title="テストケースの設計: create_e2e_testcase.md" id="section-4" number={4}>
        <WorkflowText>
          要件定義で作成した *.rfp.md を起点に、関連する *.usecases.md
          のユースケースを全て網羅するE2Eテストケースを生成します。
        </WorkflowText>

        <WorkflowSection title="起動方法" subNumber="4.1">
          <WorkflowText>
            create_e2e_testcase.md は inclusion: manual
            に設定されています。テストケースを生成したいタイミングでユーザーが手動で渡します。
          </WorkflowText>
          <WorkflowChatBubble role="human">
            #create_e2e_testcase.md 以下のrfpに対するE2Eテストケースを作成して{'\n'}
            #partner.rfp.md #member.rfp.md #client.rfp.md #project.rfp.md #contract.rfp.md
          </WorkflowChatBubble>
        </WorkflowSection>

        <WorkflowSection title="テスト範囲の分割" subNumber="4.2">
          <WorkflowInfoBox type="important">
            全rfpを一度に渡さないこと。テスト範囲を最小限に絞って実施する。
            {'\n\n'}
            rfpを大量に渡すとテストケースが膨大になり、実行時のクレジット消費が大きくなります。テストしたい機能間の依存関係に絞って最小範囲でE2Eテストケースを作成してください。
          </WorkflowInfoBox>
          <WorkflowText>
            テスト範囲が異なる複数のE2Eテストケースを作成して構いません。全機能を1つのテストケースにまとめる必要はありません。
          </WorkflowText>
          <WorkflowTable
            headers={['テストケース名', '渡すrfp', '確認範囲']}
            rows={[
              ['partner-member', '#partner.rfp.md #member.rfp.md', 'パートナー⇔要員のデータ連携'],
              [
                'client-member-partner-project-contract',
                '#partner.rfp.md #member.rfp.md #client.rfp.md #project.rfp.md #contract.rfp.md',
                '契約申請フローに必要な全エンティティの連携',
              ],
            ]}
          />
          <WorkflowText>
            partner-member
            のE2Eテストを先に実施してパートナー⇔要員間の整合性を確認し、その後で契約フロー全体のE2Eテストを実施する——という段階的なアプローチが効率的です。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="設計の観点" subNumber="4.3">
          <WorkflowTable
            headers={['観点', '内容']}
            rows={[
              ['業務フロー', '登録→参照→更新→削除のライフサイクルを再現'],
              ['クロスドメイン', 'rfp間のデータ依存関係（パートナー→要員→契約）を反映'],
              ['データパターン', '区分の違い（法人/個人事業主）、件数境界値（0/1/複数）'],
              ['ステータス遷移', '申請→承認のフルフローをカバー'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="単体テストとの棲み分け" subNumber="4.4">
          <WorkflowInfoBox type="important">
            E2Eテストに含めないこと:
            {'\n\n'}
            ・バリデーションの全パターン網羅（→ 単体テストで実施済み）
            {'\n'}
            ・認証エラー（401）、認可エラー（403）の個別確認（→ 単体テストで実施済み）
            {'\n'}
            ・単一APIの正常系/異常系の網羅（→ 単体テストで実施済み）
            {'\n\n'}
            E2Eテストは「複数ユースケースを組み合わせた業務フローの検証」に特化してください。
          </WorkflowInfoBox>
        </WorkflowSection>

        <WorkflowSection title="create_e2e_testcase.md の紹介" subNumber="4.5">
          <WorkflowCodeBlock
            title="create_e2e_testcase.md 全文を見る"
            language="markdown"
            collapsible
            defaultCollapsed
            code={`# E2Eテストケース生成ルール

## このルールの目的

指定された *.rfp.md を起点に、関連する *.proposal.md と *.usecases.md を探索し、
全てのユースケースを網羅するE2Eテストケースを生成する。

## 入力

- .kiro/requirements/**/*.rfp.md（1つ以上、ユーザーが手動で指定する）

## 出力

- .kiro/e2e_test/{テストケース名}/{テストケース名}.e2e.md

## 生成フロー

1. 関連ファイルの探索（rfp → proposal → usecases → API一覧 → API仕様書）
2. ユースケースの全量確認（全UCがテストケースでカバーされることを確認）
3. テストケースの設計（業務フロー/クロスドメイン/データパターン/エラーケース観点）
4. テストケースの出力

## テストケース設計ルール

### シナリオ構成
- 「前提条件の準備→一連のAPI呼び出し→最終状態の検証」の流れ
- API経由でデータ投入（APIで登録不可なデータのみSQL直接投入可）
- 各ステップに期待値（Response + DB状態）を記載
- curl コマンドは改行なし

### データパターンの網羅
- 権限の違い、データの有無、件数境界値、状態遷移の組み合わせ

### ユースケースの網羅性確認
- 全UCが少なくとも1つのテストケースに対応していること

## テスト実行ルール

### テスト結果の出力
- result_{yyyyMMddHHmmss}.md として出力

### バグ検知時の対応
- テストケースの期待値誤り → テストケース修正OK
- 実装のバグ → result に NG 記録のみ（実装修正しない）

## 対象外の観点
- バリデーション全網羅、認証/認可個別確認、単一API正常系/異常系

## 禁止事項
- usecases.md にないUCをテスト対象に含めない
- API登録可能データをDB直接投入しない
- 単一APIのみで構成されたケースを作成しない
- テスト実行中に実装コードを修正しない`}
          />
        </WorkflowSection>

        <WorkflowSection title="出力先" subNumber="4.6">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['配置先', '.kiro/e2e_test/{テストケース名}/'],
              ['ファイル名', '{テストケース名}.e2e.md'],
              [
                '命名規則',
                '機能の組み合わせをハイフン結合（例: client-member-partner-project-contract）',
              ],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション5: テストケースの構造 */}
      <WorkflowSection title="テストケースの構造" id="section-5" number={5}>
        <WorkflowCodeBlock
          title=".e2e.md の構造例"
          language="markdown"
          collapsible
          defaultCollapsed
          code={`# {テストケース名} E2Eテスト

## 対象機能

- {rfp名1}（{概要}）
- {rfp名2}（{概要}）

## カバーするユースケース

| UC-ID | ユースケース名 | 対応ケースNo |
|-------|----------------|--------------|
| UC-XX01 | ... | 1, 3 |

## テストケース一覧

| No | ケース名 | 確認観点 | 概要 |
|----|----------|----------|------|
| 1  | ... | 業務フロー（データ登録） | ... |

## 前提条件

（初期データ、初期ユーザー、テスト用ユーザーの準備）

## 共通前提ステップ: ユーザー準備

（ログイン、ユーザー登録、権限付与のcurl）

## ケース{No}: {ケース名}

### Step {N}: {ステップ名}

#### API呼び出し
(curl)

#### 期待値
(Response + DB確認)`}
        />
        <WorkflowText>
          テストケース生成後、「カバーするユースケース」テーブルで全UCが少なくとも1つのケースに対応していることを確認します。カバレッジの漏れがあればケースを追加します。
        </WorkflowText>
      </WorkflowSection>

      {/* セクション6: テストの実行 */}
      <WorkflowSection title="テストの実行" id="section-6" number={6}>
        <WorkflowSection title="実行ルール" subNumber="6.1">
          <WorkflowText>
            E2Eテストの実行時には local_test.md と create_e2e_testcase.md の両方を渡します。
          </WorkflowText>
          <WorkflowTable
            headers={['Steering', '役割']}
            rows={[
              ['local_test.md', 'Docker環境の構築・アプリ起動・停止手順（単体テストと共用）'],
              ['create_e2e_testcase.md', 'テスト結果の出力方法、バグ検知時の対応ルール、禁止事項'],
            ]}
          />
          <WorkflowChatBubble role="human">
            #local_test.md #create_e2e_testcase.md E2Eテストを実行して{'\n'}
            #client-member-partner-project-contract.e2e.md
          </WorkflowChatBubble>
          <WorkflowText>
            local_test.md
            は単体テスト（Phase3）と共用のルールです。Docker起動やアプリ再起動の手順はどちらのテストでも同じです。E2E固有のルール（結果ファイルの出力形式、バグ検知時に実装を修正しないルール等）は
            create_e2e_testcase.md に定義されています。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="前提データのルール" subNumber="6.2">
          <WorkflowTable
            headers={['ルール', '説明']}
            rows={[
              [
                'API経由でデータ投入',
                'データ登録APIが実装済みなら極力APIで前提データを作成。未実装APIのデータのみSQL直接投入可',
              ],
              [
                '前ケースのデータ再利用',
                'ケース1で登録したデータをケース2以降で再利用してよい（毎回クリアしない）',
              ],
              ['初期ユーザーを起点', 'ADMINでログイン→必要なユーザーを追加→各ロールでログイン'],
              ['curlは改行なし', '1行で記載'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="結果の記録" subNumber="6.3">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['配置先', '.kiro/e2e_test/{テストケース名}/'],
              ['ファイル名', 'result_{yyyyMMddHHmmss}.md（実行日時）'],
              ['記録内容', '実行日時、環境情報、結果サマリ、ケース別詳細、NG詳細'],
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション7: バグ検知時の対応 */}
      <WorkflowSection title="バグ検知時の対応" id="section-7" number={7}>
        <WorkflowSection title="E2Eテスト固有のルール" subNumber="7.1">
          <WorkflowInfoBox type="important">
            E2Eテストでは実装コードの修正を行わない。
            {'\n\n'}
            ・テストケースの期待値が誤っていれば、テストケース（.e2e.md）を修正して再実行してよい
            {'\n'}
            ・実装のバグを検知した場合でも、result ファイルに NG 内容を記録するのみとする
            {'\n'}
            ・実装修正が必要なバグは、結果レビュー後に別途対応する
          </WorkflowInfoBox>

          <WorkflowTipBox title="なぜE2Eテストでは実装を修正しないのか">
            単体テスト（Phase3）ではAIが自動でコードを修正して再テストします。これはapi_project_ruleのPhase3が「実装の品質確保」を目的としているためです。
            {'\n\n'}
            一方、E2Eテストは「業務シナリオとしての整合性確認」が目的です。E2Eテストで検知されるバグは、複数APIの組み合わせで初めて顕在化する問題であるケースが多く、修正の影響範囲が広くなりがちです。
            {'\n\n'}
            例えば「契約承認時にcontract_unit_idがNULLだと500エラー」というバグは、契約申請登録APIのバリデーション不足が原因ですが、修正にはAPI仕様書の確認、DD書の更新、バリデーション追加、テストケース修正と複数の判断が必要です。
            {'\n\n'}
            このため、E2Eテストでは:
            {'\n'}
            1. まずバグの事実を正確に記録する（result ファイル）
            {'\n'}
            2. 人間がバグの影響範囲と対応方針を判断する
            {'\n'}
            3. 判断に基づいて個別に修正対応する
            {'\n\n'}
            という手順を取ります。
          </WorkflowTipBox>
        </WorkflowSection>

        <WorkflowSection title="テストケース期待値の修正" subNumber="7.2">
          <WorkflowText>
            E2Eテストの実行中に「テストケースの期待値が実際のAPI仕様と合っていない」ケースが発生することがあります。これはバグではなくテストケース自体の不備です。
          </WorkflowText>
          <WorkflowTable
            headers={['修正カテゴリ', '内容']}
            rows={[
              ['フィールド名の差異', 'テストケースでは costPrice と記載→実際のAPIは costAmount'],
              ['レスポンス構造の差異', 'フラットなフィールドを期待→実際は result キーでラップ'],
              ['master_type コード値', 'テストケースでは SES と記載→実際のDBでは QUASI_MANDATE'],
              [
                'デフォルト値の未指定',
                '画面のデフォルト値（contractUnitId: "UNSELECTED"）の指定漏れ',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="対応フローの全体像" subNumber="7.3">
          <WorkflowTable
            headers={['ケース', '対応']}
            rows={[
              ['テストケースの期待値誤り', '.e2e.md を修正 → 再実行'],
              [
                '実装のバグ',
                'result ファイルに NG 記録（実装修正なし）→ 人間がレビュー後に対応判断',
              ],
            ]}
          />
          <WorkflowText>人間がレビュー後に判断する修正パターン：</WorkflowText>
          <WorkflowList
            items={[
              'バリデーション不足 → API仕様書確認 → DD書修正 → コード修正',
              'ステータス遷移の不備 → UseCase DD書修正 → コード修正',
              'DB制約の不整合 → スキーマ定義見直し → マイグレーション追加',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション8: 本プロジェクトでの実体験 */}
      <WorkflowSection title="本プロジェクトでの実体験" id="section-8" number={8}>
        <WorkflowSection title="テストケース名と対象" subNumber="8.1">
          <WorkflowTable
            headers={['項目', '内容']}
            rows={[
              ['テストケース名', 'client-member-partner-project-contract'],
              ['対象機能', 'パートナー、要員、取引先、プロジェクト、契約（5機能）'],
              ['カバーUC数', '26ユースケース'],
              ['テストケース数', '9ケース'],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="実行結果サマリ" subNumber="8.2">
          <WorkflowTable
            headers={['実行', '結果', '内容']}
            rows={[
              [
                '1回目',
                'NG（2件）',
                'BUG-1: 契約承認時にcontract_unit_idがNULLで500エラー / BUG-2: テンプレートアイテム更新で重複キーエラー',
              ],
              [
                '2回目（再試験）',
                'OK',
                'テストケース修正（contractUnitId指定、selectItemId指定）で全件通過',
              ],
            ]}
          />
        </WorkflowSection>

        <WorkflowSection title="検知されたバグの実例" subNumber="8.3">
          <WorkflowText>
            BUG-1: 契約申請承認時の500エラー — contractUnitId
            を指定せず登録した契約申請を承認すると、contract テーブルへのINSERT時にNOT
            NULL制約違反。画面ではデフォルトで「UNSELECTED」が設定されるが、API仕様上は任意項目。テストケースに
            "contractUnitId":"UNSELECTED" を明示指定して再試験→OK。
          </WorkflowText>
          <WorkflowText>
            BUG-2: テンプレートアイテム更新の500エラー —
            既存selectItemsを含むリクエストを送信すると重複キー（item_key +
            code）エラー。更新処理が全件INSERTしようとするため既存データと競合。既存selectItemsに
            selectItemId を付与（UPDATE扱い）して再試験→OK。
          </WorkflowText>
        </WorkflowSection>

        <WorkflowSection title="テストケース精度の課題" subNumber="8.4">
          <WorkflowText>
            E2Eテストケースは proposal と usecases
            をインプットに生成します。そのため、詳細設計レベルの仕様（例: selectItemId
            がnullならINSERT、設定済みならUPDATE）はテストケースに反映されません。
          </WorkflowText>
          <WorkflowText>
            一方、DD書まで参照させるとAIが詳細設計レベルの分岐を通すテストケースを作成してしまい、E2Eテストのスコープ（業務フローの検証）から逸脱するリスクがあります。
          </WorkflowText>
          <WorkflowText>
            本プロジェクトでは、テストケース生成後にユーザーが事前準備やリクエストデータを手動で修正する運用で対処しました。テストケース作成時のデータ精度をどこまで自動化できるかは今後の課題です。
          </WorkflowText>
        </WorkflowSection>
      </WorkflowSection>

      {/* セクション9: 関連するステアリングファイル */}
      <WorkflowSection title="関連するステアリングファイル" id="section-9" number={9}>
        <WorkflowTable
          headers={['ファイル', 'E2Eテストでの役割']}
          rows={[
            ['create_e2e_testcase.md', 'テストケースの設計ルール（手動起動）'],
            ['local_test.md', 'Docker環境の構築手順、アプリ起動手順（手動起動）'],
          ]}
        />
      </WorkflowSection>

      {/* セクション10: 注意すべきこと */}
      <WorkflowSection title="注意すべきこと" id="section-10" number={10}>
        <WorkflowTable
          headers={['注意点', '説明']}
          rows={[
            [
              '単体テスト完了後に実施',
              '対象APIの単体テストが全件OKであることが前提。単体テスト未完了のAPIをE2Eに含めない',
            ],
            [
              '実装コードを修正しない',
              'バグを検知してもresultに記録するのみ。修正は人間が判断してから',
            ],
            [
              'テストケース期待値は修正OK',
              'API仕様と合わない期待値はテストケース側を修正して再実行',
            ],
            ['ユースケース網羅性の確認', '全UCが少なくとも1ケースに対応していること'],
            ['前ケースのデータ依存に注意', 'ケース間でデータを再利用するため、実行順序を守ること'],
          ]}
        />
      </WorkflowSection>

      {/* セクション11: 成果物まとめ */}
      <WorkflowSection title="成果物まとめ" id="section-11" number={11}>
        <WorkflowOutputBox>
          <WorkflowTable
            headers={['成果物', '配置先', '利用先']}
            rows={[
              ['{テストケース名}.e2e.md', '.kiro/e2e_test/{テストケース名}/', 'E2Eテスト仕様書'],
              [
                'result_{yyyyMMddHHmmss}.md',
                '.kiro/e2e_test/{テストケース名}/',
                'テスト結果の記録',
              ],
            ]}
          />
        </WorkflowOutputBox>
      </WorkflowSection>

      {/* セクション12: ベストプラクティスとアンチパターン */}
      <WorkflowSection title="ベストプラクティスとアンチパターン" id="section-12" number={12}>
        <WorkflowPracticeBox
          type="best"
          items={[
            {
              practice: 'テスト範囲を機能間の依存関係に絞って最小化する',
              effect:
                'rfpを大量に渡すとテストケースが膨大になりクレジット消費が増大する。段階的に範囲を広げる',
            },
            {
              practice: 'usecases.md の全UCカバレッジを確認してから実行する',
              effect:
                'テストケース生成後に「カバーするユースケース」テーブルで漏れがないことを確認する',
            },
            {
              practice: 'テストケース期待値は事前にAPI仕様書と突合する',
              effect:
                '詳細設計レベルの仕様（upsert判定等）は自動生成で漏れやすいため、手動で補完する',
            },
            {
              practice: 'バグ検知時はresultに正確に記録し、実装修正は別途判断する',
              effect: 'E2Eテストでの修正は影響範囲が広いため、人間が方針を判断してから対応する',
            },
            {
              practice: '段階的にE2Eテストを実施する（小→大）',
              effect:
                '2機能間の連携を先に確認し、その後フロー全体のE2Eに進むと問題の切り分けが容易になる',
            },
            {
              practice: '作成した .e2e.md をリグレッション試験として再利用する',
              effect: '既存APIの修正後に再実行することで、修正の副作用を検知できる',
            },
          ]}
        />

        <WorkflowPracticeBox
          type="anti"
          items={[
            {
              practice: '全rfpを一度に渡してE2Eテストケースを生成する',
              effect:
                'テストケースが膨大になり、実行時のクレジット消費と失敗時の原因特定コストが増大する。機能間の依存関係に絞って最小範囲で生成する',
            },
            {
              practice: '単体テスト未完了のAPIをE2Eに含める',
              effect:
                '個別APIのバグがE2Eシナリオ全体を壊し、問題の切り分けが困難になる。対象APIの単体テストが全件OKであることを前提とする',
            },
            {
              practice: 'E2Eテストで実装コードを修正する',
              effect:
                '影響範囲を考慮しない場当たり修正になり、他の業務フローを壊すリスクがある。resultに記録し、人間が影響範囲を判断してから別途修正する',
            },
            {
              practice: '単体テストの観点をE2Eテストに含める',
              effect:
                'バリデーション全網羅や認証/認可の個別確認はE2Eのスコープではなく、ケース数が肥大化する。E2Eは業務フローの検証に特化する',
            },
            {
              practice: 'ケース間のデータ依存を無視して実行順序を変える',
              effect:
                '前ケースで作成したデータが存在せず、後続ケースが全て失敗する。テストケースの実行順序を守る',
            },
            {
              practice: 'テストケースの期待値誤りと実装バグを区別せずに対応する',
              effect:
                'テストケース側の不備なのにコード修正に着手し、不要な変更が入る。まず期待値がAPI仕様と合っているか確認する',
            },
          ]}
        />
      </WorkflowSection>

      {/* セクション13: バックエンド開発編の全体まとめ */}
      <WorkflowSection title="バックエンド開発編の全体まとめ" id="section-13" number={13}>
        <WorkflowText>Part6が完了すると、バックエンド開発の全工程が完了です。</WorkflowText>
        <WorkflowTable
          headers={['フェーズ', '成果物', '内容']}
          rows={[
            ['Part1', 'スキーマ定義、API仕様書', 'DB設計とAPI I/Fの定義'],
            ['Part2', 'api_project_rule の理解', 'ワークフロー全体像の把握'],
            ['Part3（Phase1）', 'DD書、instructions.md', '設計書の作成'],
            ['Part4（Phase2）', 'Javaコード、マイグレーション', 'コード生成'],
            ['Part5（Phase3）', 'test.md、test_result.md', '単体テスト'],
            ['Part6', '.e2e.md、result.md', 'E2Eテスト'],
          ]}
        />

        <WorkflowSection title="最終的に得られるもの" subNumber="13.1">
          <WorkflowList
            items={[
              'スキーマ定義書（全テーブル）',
              'API仕様書（全API）',
              'DD書（コードと整合済み）',
              'Javaソースコード（ビルド＆テスト通過済み）',
              'Flywayマイグレーション',
              '単体テスト結果（全API × 全ケースOK）',
              'E2Eテスト結果（業務シナリオ全件OK）',
              'API一覧・権限一覧・用語集（最新化済み）',
            ]}
          />
        </WorkflowSection>
      </WorkflowSection>
    </WorkflowArticleLayout>
  );
};

export default WorkflowBeStep6;
